import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";
import Modal, { MODAL_SIZE } from "components/Modal";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import ChallengeItemSidebar from "./ChallengeItemSidebar";
import ChallengeItemDetailView from "./ChallengeItemDetailView";
import { useChallengeModal } from "hooks";

/**
 * CodeChallengeItemsModal
 *
 * @param {Object}    challengeDetails
 * @param {Boolean}   show
 * @param {Function}  onClose
 */
const CodeChallengeItemsModal = ({ challengeDetails, show, onClose, ...props }) => {
  const { name, disciplines = [], types = [], skills = [] } = challengeDetails || {};

  const {
    sortedChallengeItems,
    activeItemIndex,
    setActiveItemIndex,
    activeItem,
    collapsedGroups,
    stats,
    categorizedTasks,
    toggleGroup,
    handlePrev,
    handleNext,
  } = useChallengeModal(challengeDetails, show);

  return (
    <Modal
      show={show}
      showSave={false}
      showCancel
      onClose={onClose}
      size={MODAL_SIZE.large}
      secondaryButtonMessageId="Global.Close"
      title={<ModalHeaderTitle name={name} disciplines={disciplines} types={types} skills={skills} />}
      {...props}
    >
      <div css={styles.dashboardContainer}>
        <ChallengeItemSidebar
          stats={stats}
          categorizedTasks={categorizedTasks}
          collapsedGroups={collapsedGroups}
          activeItemIndex={activeItemIndex}
          setActiveItemIndex={setActiveItemIndex}
          toggleGroup={toggleGroup}
          totalItemsCount={sortedChallengeItems.length}
        />
        <ChallengeItemDetailView
          activeItem={activeItem}
          activeItemIndex={activeItemIndex}
          totalItemsCount={sortedChallengeItems.length}
          handlePrev={handlePrev}
          handleNext={handleNext}
        />
      </div>
    </Modal>
  );
};

/**
 * ModalHeaderTitle
 * @param {String} name
 * @param {Array} disciplines
 * @param {Array} types
 * @param {Array} skills
 */
const ModalHeaderTitle = ({ name, disciplines = [], types = [], skills = [] }) => (
  <div css={styles.modalTitleContainer}>
    <div css={styles.modalMainTitle}>{name}</div>
    {(types.length > 0 || disciplines.length > 0 || skills.length > 0) && (
      <div css={styles.modalSubtitle}>
        {types.map((t) => (
          <span key={t.id} css={styles.modalSubtitleItem}>
            <Icon type={ICON_TYPE.settings} size={ICON_SIZE.xsmall} color={colors.grayAnatomyLight1} /> {t.name}
          </span>
        ))}
        {disciplines.map((d) => (
          <span key={d.id} css={styles.modalSubtitleItem}>
            <Icon type={ICON_TYPE.user} size={ICON_SIZE.xsmall} color={colors.grayAnatomyLight1} /> {d.name}
          </span>
        ))}
        {skills.map((s) => (
          <span key={s.id} css={styles.modalSubtitleItem}>
            <Icon type={ICON_TYPE.star} size={ICON_SIZE.xsmall} color={colors.grayAnatomyLight1} /> {s.name}
          </span>
        ))}
      </div>
    )}
  </div>
);

ModalHeaderTitle.propTypes = {
  name: PropTypes.string.isRequired,
  disciplines: PropTypes.array,
  types: PropTypes.array,
  skills: PropTypes.array,
};

CodeChallengeItemsModal.propTypes = {
  challengeDetails: PropTypes.object,
  show: PropTypes.bool,
  onClose: PropTypes.func,
};

// Co-located Orchestrator Layout Styles
const styles = {
  modalTitleContainer: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,
  modalMainTitle: css`
    font-size: ${fontSize.large};
    font-weight: 700;
    color: ${colors.grayAnatomyDark};
  `,
  modalSubtitle: css`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  `,
  modalSubtitleItem: css`
    font-size: ${fontSize.xxsmall};
    color: ${colors.grayAnatomyBase};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: normal;
  `,
  dashboardContainer: css`
    display: flex;
    height: 75vh;
    border-radius: 8px;
    background-color: ${colors.white};
    overflow: hidden;
  `,
};

export default CodeChallengeItemsModal;
